import {useState} from 'react';
import {commitTicketingMutation} from '../../../graphql/forecastRelay';
import CreateForecastTaskMutation from '../../../../../mutations/ticketing/tickets/createForecastTaskMutation';

export interface CreateForecastTaskInput {
	name: string;
	projectId?: string | null;
	assignedPersonIds?: string[] | null;
	dueDate?: string | null; // YYYY-MM-DD
	description?: string | null;
}

// Split a YYYY-MM-DD string into Forecast's deadline Y/M/D triple. Kept local to
// the ticketing island: shared/util/DateUtil transitively pulls the
// design-system graph, which the island deliberately avoids (and which breaks
// the stubbed jest env — see memory web-front-jest-design-system-stub-failures).
function deadlinePartsFromIsoDate(isoDate: string | null | undefined): {
	year: number | null;
	month: number | null;
	day: number | null;
} {
	const m = isoDate ? /^(\d{4})-(\d{2})-(\d{2})$/.exec(isoDate) : null;
	if (!m) return {year: null, month: null, day: null};
	return {year: Number(m[1]), month: Number(m[2]), day: Number(m[3])};
}

export interface CreatedForecastTask {
	id: string;
	companyTaskId: number | null;
	name: string | null;
}

export function useCreateForecastTask() {
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState<string | null>(null);

	function submit(input: CreateForecastTaskInput): Promise<CreatedForecastTask> {
		setError(null);
		setLoading(true);
		const {year, month, day} = deadlinePartsFromIsoDate(input.dueDate);
		const mutationInput = {
			name: input.name,
			projectId: input.projectId || null,
			// Send [] rather than null when no assignee is chosen: an explicit null
			// crashes service-company's TaskValidationLogic (isAssignedPersonsSet
			// becomes true while the value is null → NPE → 500 on /task).
			assignedPersons: input.assignedPersonIds && input.assignedPersonIds.length > 0 ? input.assignedPersonIds : [],
			description: input.description || null,
			deadlineYear: year,
			deadlineMonth: month,
			deadlineDay: day,
		};
		return new Promise((resolve, reject) => {
			commitTicketingMutation(
				CreateForecastTaskMutation,
				mutationInput,
				response => {
					setLoading(false);
					const node = response.createTask?.task?.node;
					if (!node) {
						const msg = 'Task creation returned no task';
						setError(msg);
						reject(new Error(msg));
						return;
					}
					resolve({id: node.id, companyTaskId: node.companyTaskId ?? null, name: node.name ?? null});
				},
				err => {
					setLoading(false);
					setError(err instanceof Error ? err.message : String(err));
					reject(err);
				}
			);
		});
	}

	return {submit, loading, error};
}
